import {
  Alert,
  Box,
  Button,
  FormControl,
  TextField,
  Typography,
  Paper,
  CircularProgress,
} from '@mui/material';
import { useContext, useState, useEffect } from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import { AuthContext } from '../../contexts/user-context/user-context';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINT } from '../../configurations/global.config';
import { format_phone_number } from '../../utils/phone_numbers';
import { UserType } from '../../enums/users';
import { User } from '../../interfaces/common/authentication';
import { passwordValidationError } from './utils';
import { motion } from 'framer-motion';
import { EmojiEmotions } from '@mui/icons-material';
import { OrgSettingsContext } from '../../contexts/org-settings-context';
import Countdown, { zeroPad } from 'react-countdown';
import { useSnackBar } from '../../contexts/snackbar';
import { APP_VERSION_HEADER } from '../../utils/app_authentication';
import PasswordField from '../common/password-field/password-field';
import { set } from 'lodash';

interface LoginWithOTPProps {
  account_id: string;
  password: string;
  phone_number: string;
  firstLogin: boolean;
  onBack: () => void;
  onSuccess: (data: any) => void;
  invitedName: string;
}

function LoginWithOTP({
  account_id,
  password,
  phone_number,
  firstLogin,
  onBack,
  onSuccess,
  invitedName,
}: LoginWithOTPProps) {
  const { setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [otp, setOtp] = useState('');
  const [newPass, setNewPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendAllowed, setResendAllowed] = useState(false);
  const [resendCountdown, setResendCountdown] = useState<JSX.Element>();
  const { color } = useContext(OrgSettingsContext);
  const { showMessage } = useSnackBar();

  // Handle OTP resend request
  const handleResendOTP = async () => {
    setResendLoading(true);
    setMessage('');

    const payload = {
      account_id,
      password: password,
    };

    try {
      const res = await axios.post(`${API_ENDPOINT}/v1/auth/token/`, payload, {
        headers: APP_VERSION_HEADER,
      });
      showMessage('Something went wrong. Please try again later.', 'error');
    } catch (err: any) {
      const res = err.response;
      if (!res || !res.data) {
        setMessage('Unexpected error. Please contact support.');
      } else if (res.data.code == 'first_login_otp' || res.data.code == 'otp_required') {
        showMessage('Sent new code', 'success');
        setResendAllowed(false);
        startCountdown();
      } else {
        showMessage('Failed to send code', 'error');
        setMessage(
          'Failed to send you a new code. Please try again in a moment or contact us for help.'
        );
      }
    } finally {
      setResendLoading(false);
    }
  };

  // Start countdown timer (2 minutes)
  const startCountdown = () => {
    setResendCountdown(
      <Countdown
        date={Date.now() + 60 * 1000}
        onComplete={() => setResendAllowed(true)}
        renderer={({ minutes, seconds }) => (
          <Typography variant="body2" color="text.secondary">
            Resend available in {zeroPad(minutes)}:{zeroPad(seconds)}
          </Typography>
        )}
      />
    );
  };

  // Initialize countdown on component mount
  useEffect(() => {
    startCountdown();
  }, []);

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setMessage('');

    if (!otp) {
      setMessage('OTP is required.');
      return;
    }

    if (firstLogin) {
      const err = passwordValidationError(newPass, confirmPass);
      if (err) {
        setMessage(err);
        return;
      }
    }

    setIsLoading(true);
    const payload = {
      account_id,
      otp,
      password: firstLogin ? newPass : password,
    };

    try {
      const res = await axios.post(`${API_ENDPOINT}/v1/auth/token/`, payload);
      onSuccess(res.data);
    } catch (err: any) {
      const res = err.response;
      const detail = res?.data?.detail || 'Invalid credentials';
      setMessage(detail);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        maxWidth: 480,
        margin: 'auto',
        padding: 4,
        borderRadius: 3,
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        backgroundColor: '#ffffff',
      }}
    >
      {!invitedName && (
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Secure Login
        </Typography>
      )}
      {invitedName && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: -30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
            }}
          >
            <motion.div
              initial={{ rotate: -10 }}
              animate={{ rotate: [0, 10, -10, 10, 0] }}
              transition={{
                duration: 1.8,
                ease: 'easeInOut',
                repeat: Infinity,
              }}
            >
              <EmojiEmotions sx={{ fontSize: 50, color: color }} />
            </motion.div>
            <Box>
              <Typography variant="h5" fontWeight={700} sx={{ color: '#333' }}>
                One Last Step
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Please enter the verification code sent to <strong>{phone_number}</strong>, and create a new password.
              </Typography>
            </Box>
          </Box>
        </motion.div>
      )}

      <form onSubmit={handleSubmit}>
        {message && (
          <Alert severity={message.includes('sent') ? 'success' : 'error'} sx={{ mb: 2, borderRadius: 1 }}>
            {message}
          </Alert>
        )}

        <FormControl fullWidth sx={{ mt: 2 }}>
          <TextField
            label="Verification Code"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            required
            variant="outlined"
            InputProps={{
              endAdornment: (
                <Button
                  size="small"
                  disabled={!resendAllowed || resendLoading}
                  onClick={handleResendOTP}
                  sx={{
                    textTransform: 'none',
                    color: resendAllowed ? color : 'text.secondary',
                    minWidth: 'auto',
                  }}
                >
                  {resendLoading ? (
                    <CircularProgress size={20} />
                  ) : resendAllowed ? (
                    'Resend Code'
                  ) : (
                    resendCountdown
                  )}
                </Button>
              ),
            }}
          />
        </FormControl>

        {firstLogin && (
          <Box sx={{ mt: 2 }}>
            <PasswordField
              withConfirmation={true}
              label="New Password"
              confirmLabel="Confirm New Password"
              placeholder="Enter a new password"
              value={newPass}
              confirmValue={confirmPass}
              onPasswordChange={(value) => setNewPass(value)}
              onConfirmChange={(value) => setConfirmPass(value)}
              variant="outlined"
            />
          </Box>
        )}

        <Box display="flex" gap={2} mt={4}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ borderRadius: 2, fontWeight: 600 }}
            disabled={
              isLoading ||
              !otp ||
              (firstLogin && (!newPass || !confirmPass))
            }
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : firstLogin ? (
              'Set Password'
            ) : (
              'Login'
            )}
          </Button>

          <Button
            fullWidth
            variant="outlined"
            onClick={onBack}
            sx={{ borderRadius: 2 }}
            disabled={isLoading}
          >
            Go Back
          </Button>
        </Box>
      </form>
    </Paper>
  );
}

export default LoginWithOTP;
